import {
  Formik,
  Field,
  FormikHelpers,
} from 'formik';
import i18n from 'i18next';
import * as Yup from 'yup';
import {
  useNavigate,
} from 'react-router';

import {
  Icon,
  Modal,
  Button,
  HelpText,
  TextField,
} from 'web-components';
import {
  Flex,
  Text,
} from 'web-styled';

import {
  getNamespaceSymbol,
  trackAnalyticsEvent,
} from 'web-utils'

import * as S from './styled'

interface ClientFormValues {
  email: string;
  first_name: string;
  last_name: string;
  invite: boolean;
}

interface AddClientProps {
  isOpen: boolean;
  isLimitReached: boolean;
  onSubmit: (data: ClientFormValues, actions: FormikHelpers<ClientFormValues>) => void;
  onRequestClose: () => void;
}

const INITIAL_FORM_VALUES: ClientFormValues = {
  email: '',
  first_name: '',
  last_name: '',
  invite: false,
};

const validationSchema = Yup.object().shape({
  email: Yup.string().when('invite', {
    is: true,
    then: Yup.string()
      .email('Email must be a valid email')
      .required('Email is required'),
    otherwise: Yup.string()
      .email('Email must be a valid email'),
  }),
  first_name: Yup.string()
    .required('First name is required'),
  last_name: Yup.string()
    .required('Last name is required'),
});

const CrownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="78"
    height="78"
    fill="none"
    viewBox="0 0 78 78"
  >
    <path
      fill="#2773C1"
      fillOpacity="0.32"
      d="M39 78c21.54 0 39-17.46 39-39S60.54 0 39 0 0 17.46 0 39s17.46 39 39 39"
    />
    <g fill="#fff" opacity="0.8">
      <path d="m18.8 46.578-2.783-18.072a1.51 1.51 0 0 1 .716-1.52 1.54 1.54 0 0 1 .854-.21c.298.014.585.117.823.297l8.663 6.498c.227.166.484.287.756.356a2.1 2.1 0 0 0 1.615-.278c.235-.153.437-.352.593-.585l7.215-10.822a1.921 1.921 0 0 1 3.198 0l7.21 10.813c.158.233.36.434.595.59a2.072 2.072 0 0 0 2.37-.083l8.662-6.494a1.507 1.507 0 0 1 2.394 1.433l-2.779 18.072zM18.8 49.921H58.9v4.593a2.1 2.1 0 0 1-.61 1.462 2.2 2.2 0 0 1-.677.453 2.2 2.2 0 0 1-.804.156H20.89a2.08 2.08 0 0 1-2.09-2.091z" />
    </g>
  </svg>
);

const IdeafitUpgradeModal = ({
  isOpen,
  onRequestClose
}: Pick<AddClientProps, 'isOpen' | 'onRequestClose'>) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    trackAnalyticsEvent('clicked_upgrade_plan');
    navigate('/payments/details');
    onRequestClose();
  };

  return (
    <Modal
      hideHeader
      isOpen={isOpen}
      contentStyle={{
        width: 480,
        marginTop: 'auto',
        borderRadius: 15,
      }}
      onRequestClose={onRequestClose}
    >
      <Flex column width="100%" align="center" spacing={20}>
        <S.Close onClick={onRequestClose}>
          <Icon
            type="fas fa-times"
          />
        </S.Close>
        <Flex column width="100%" align="center" spacing={10}>
          <CrownIcon />
          <Text size="24px" weight="700" color="#121827">
            Upgrade Now
          </Text>
        </Flex>
        <Text size="16px" color="#121827" align="center">
          You have reached the client limit for the free version of the program builder.
          Upgrade to Program Builder + now to unlock unlimited clients, results logging, and more.
        </Text>
        <Flex column width="100%" align="center">
          <S.PrimaryButton onClick={handleUpgrade}>
            Upgrade Now
          </S.PrimaryButton>
          <Button
            as="a"
            rel="noopener noreferrer"
            target="_blank"
            spacing="big"
            appearance="link"
            href="https://www.ideafit.com/program-builder"
          >
            Learn more
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

const DefaultLimitReachedModal = ({
  isOpen,
  onRequestClose
}: Pick<AddClientProps, 'isOpen' | 'onRequestClose'>) => (
  <Modal
    title={i18n.t('add.client.modal.reached')}
    isOpen={isOpen}
    contentStyle={{ width: 600 }}
    onRequestClose={onRequestClose}
  >
    <Flex column width="100%" align="center" spacing={15}>
      <Text align="center">
        {i18n.t('add.client.modal.reached.body')}
      </Text>
      <Button
        ghost
        type="submit"
        spacing="large"
        appearance="primary"
        onClick={onRequestClose}
      >
        Ok
      </Button>
    </Flex>
  </Modal>
);

const ClientFormModal = ({
  isOpen,
  onSubmit,
  onRequestClose
}: Omit<AddClientProps, 'isLimitReached'>) => {
  const handleSubmitWithInvite = (
    formikProps: any,
    invite: boolean
  ) => {
    formikProps.setValues({
      ...formikProps.values,
      invite,
    });
    setTimeout(() => {
      formikProps.submitForm();
    }, 10);
  };

  return (
    <Modal
      title={i18n.t('add.client.modal.title')}
      isOpen={isOpen}
      contentStyle={{ width: 500 }}
      onRequestClose={onRequestClose}
    >
      <Formik
        initialValues={INITIAL_FORM_VALUES}
        validateOnBlur={false}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Flex
            column
            width="100%"
            as="form"
            spacing={10}
            onSubmit={formikProps.handleSubmit}
          >
            <Field
              autoFocus
              name="first_name"
              label="First Name *"
              value={formikProps.values.first_name}
              placeholder="e.g. John"
              component={TextField}
              autoComplete="off"
            />
            <Field
              name="last_name"
              label="Last Name *"
              value={formikProps.values.last_name}
              placeholder="e.g Smith"
              component={TextField}
              autoComplete="off"
            />
            <Field
              name="email"
              label={`${i18n.format(i18n.t('client'), 'cap')}'s email`}
              value={formikProps.values.email}
              placeholder="Email"
              component={TextField}
              autoComplete="off"
            />
            <Flex width="100%" margin="10px 0" justify="center" spacing={30}>
              <Button
                ghost
                type="button"
                spacing="large"
                appearance="primary"
                isLoading={formikProps.isSubmitting && !formikProps.values.invite}
                isDisabled={formikProps.isSubmitting}
                onClick={() => handleSubmitWithInvite(formikProps, false)}
              >
                Invite later
              </Button>
              <Button
                type="button"
                appearance="success"
                onClick={() => handleSubmitWithInvite(formikProps, true)}
                spacing="large"
                isLoading={formikProps.isSubmitting && formikProps.values.invite}
                isDisabled={formikProps.isSubmitting}
              >
                Invite now
              </Button>
            </Flex>
            <hr />
            <HelpText size="13px" html={i18n.t('add.client.modal.help')} />
          </Flex>
        )}
      </Formik>
    </Modal>
  );
};

const AddClient = ({
  isOpen,
  onSubmit,
  isLimitReached,
  onRequestClose,
}: AddClientProps) => {
  const namespaceSymbol = getNamespaceSymbol();

  if (isLimitReached) {
    return namespaceSymbol === 'ideafit' ? (
      <IdeafitUpgradeModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
      />
    ) : (
      <DefaultLimitReachedModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
      />
    );
  }

  return (
    <ClientFormModal
      isOpen={isOpen}
      onSubmit={onSubmit}
      onRequestClose={onRequestClose}
    />
  );
};

export default AddClient;
